import DataIcon from "assets/images/defaultIcons/data.svg";
import LocationIcon from "assets/images/defaultIcons/location.svg";
import TeamIcon from "assets/images/defaultIcons/team.svg";
import theme from "assets/theme";
import { formatDatetime } from "helpers/shared.helpers";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import AlertService from "services/Alert.service";
import {
  ALERT_TYPE_CRITICAL,
  ALERT_TYPE_INFO,
  INFO_TYPE_REMINDER,
} from "shared/constants";
import { smallAppIconSize } from "web/constants";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import CustomDateTimePicker from "webComponentsFormElements/inputs/CustomDateTimePicker.component";
import CustomTextEditor from "webComponentsFormElements/inputs/CustomTextEditor.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import LocationSelectionModal from "webComponentsFormElements/inputs/LocationSelectionModal.component";
import SystemSelectionModal from "webComponentsFormElements/inputs/SystemSelectionModal.component";
import TeamSelectionModal from "webComponentsFormElements/inputs/TeamSelectionModal.component";
import UserSelectionModal from "webComponentsFormElements/inputs/UserSelectionModal.component";
import AlertRecipientSummary from "webComponentsScreens/alerts/AlertRecipientSummary.component";
import { AlertContext } from "webContext/Alert.context";
import { ConfirmationContext } from "webContext/Confirmation.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";
import PropTypes from "prop-types";

const classes = StyleSheet.create({
  root: {
    flexDirection: "row",
    gap: 20,
  },
  title: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    paddingLeft: 8,
    paddingRight: 8,
    paddingBottom: 8,
    paddingTop: 16,
  },
  userSelectionContainer: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 10,
  },
  affectedPlatformsContainer: {
    maxHeight: 150,
  },
  content: {
    fontSize: 14,
    fontWeight: theme.font.weight.regular,
    color: theme.color.darkGrey,
    paddingLeft: 24,
    paddingBottom: 8,
  },
  noneText: {
    fontStyle: "italic",
  },
  icon: {
    width: smallAppIconSize,
    height: smallAppIconSize,
    fill: theme.color.white,
    marginRight: 4,
    alignSelf: "flex-start",
  },
  linksSection: {
    marginTop: 8,
    padding: 10,
    backgroundColor: theme.color.lightGrey,
    borderRadius: 6,
    gap: 8,
  },
  linksSectionTitle: {
    fontWeight: theme.font.weight.bold,
    color: theme.color.darkGrey,
    marginBottom: 4,
  },
  linkRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  linkInput: {
    flex: 1,
    backgroundColor: theme.color.white,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: theme.color.grey,
    color: theme.color.darkGrey,
    fontSize: 14,
  },
  removeLinkText: {
    color: theme.color.error,
    fontWeight: theme.font.weight.bold,
    fontSize: 18,
    paddingHorizontal: 4,
  },
  addLinkText: {
    color: theme.color.primary,
    fontWeight: theme.font.weight.bold,
    fontSize: 14,
    marginTop: 4,
  },
  historySection: {
    marginTop: 8,
    gap: 8,
  },
  historyItem: {
    backgroundColor: theme.color.lightGrey,
    borderRadius: 6,
    padding: 10,
  },
  historyMeta: {
    fontSize: 13,
    fontStyle: "italic",
    color: theme.color.darkGrey,
    marginTop: 6,
  },
  addDetailsText: {
    color: theme.color.primary,
    fontWeight: theme.font.weight.bold,
    fontSize: 14,
    marginTop: 8,
  },
});

function formatEditMeta(version) {
  const user = version.createUser || {};
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  const author = name || user.email || "Unknown";
  const when = version.createDatetime
    ? moment(version.createDatetime).format("MMM D, YYYY h:mm A")
    : "unknown date";
  return `Edited by ${author} · ${when}`;
}

const extractAffectedSystems = (alert) => {
  return alert._associations.systemAlertMap || [];
};

const extractTeamIds = (alert) => {
  return alert._associations.teamAlertMap.map((alertMap) => alertMap.teamId);
};

const extractLocationIds = (alert) => {
  return (
    alert._associations.locationAlertMap?.map(
      (alertMap) => alertMap.locationId,
    ) || []
  );
};

export default function CreateAlertModal(props) {
  const {
    visible,
    onClose: parentOnClose,
    existingAlert,
    alertType: alertTypeProp,
    showDeleted = false,
  } = props;

  const alertTypeRef = useRef(alertTypeProp);
  if (alertTypeProp !== null) {
    // eslint-disable-next-line react-hooks/refs -- reused modal intentionally remembers the last non-null alertType across renders
    alertTypeRef.current = alertTypeProp;
  }
  const alertType =
    // eslint-disable-next-line react-hooks/refs -- see above
    existingAlert?.type || alertTypeRef.current || ALERT_TYPE_CRITICAL;
  const isInfoMessage = alertType === ALERT_TYPE_INFO;

  const {
    teamList,
    personsByTeam,
    personList,
    organizationSystemMap,
    getOrganizationSystems,
    locationList,
    personsByLocation,
  } = useContext(OrganizationContext);
  const { getAlerts } = useContext(AlertContext);
  const { showToast } = useContext(ToastContext);
  const { showConfirmation } = useContext(ConfirmationContext);

  const [isSending, setIsSending] = useState(false);
  const [hasUnsavedChangesInfo, setHasUnsavedChangesInfo] = useState(false);
  const [hasOpenedSelectionModal, setHasOpenedSelectionModal] = useState(false);

  const [isSystemSelectionOpen, setIsSystemSelectionOpen] = useState(false);
  const [isTeamSelectionOpen, setIsTeamSelectionOpen] = useState(false);
  const [isLocationSelectionOpen, setIsLocationSelectionOpen] = useState(false);
  const [isUserSelectionOpen, setIsUserSelectionOpen] = useState(false);

  const [selectedSystemIds, setSelectedSystemIds] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);

  const [systemIsOnlineMap, setSystemIsOnlineMap] = useState({});

  const [alertData, setAlertData] = useState({});
  const [links, setLinks] = useState([]);
  const [newDescription, setNewDescription] = useState("");
  const [isDetailsEditorShown, setIsDetailsEditorShown] = useState(false);

  const descriptionHistory = useMemo(
    () => existingAlert?._associations?.descriptionHistory || [],
    [existingAlert],
  );

  const conditionalButtonTitle = existingAlert ? "Save" : "Send";
  let conditionalTitle;
  if (existingAlert) {
    conditionalTitle = isInfoMessage ? "Edit Info Message" : "Edit Alert";
  } else {
    conditionalTitle = isInfoMessage ? "Create Info Message" : "Create an Alert";
  }

  const isTeamComplete = useCallback(
    (teamId, selectedUserIds) => {
      const teamMembers = personsByTeam[teamId] || [];
      const personIdsInTeam = teamMembers.map((user) => user.personId);
      return (
        personIdsInTeam.length > 0 &&
        personIdsInTeam.every((personId) => selectedUserIds.includes(personId))
      );
    },
    [personsByTeam],
  );

  const isLocationComplete = useCallback(
    (locationId, selectedUserIds) => {
      const locationMembers = personsByLocation[locationId] || [];
      const personIdsInLocation = locationMembers.map((user) => user.personId);

      return (
        personIdsInLocation.length > 0 &&
        personIdsInLocation.every((personId) =>
          selectedUserIds.includes(personId),
        )
      );
    },
    [personsByLocation],
  );

  const filterTeamIdsByPerson = useCallback((teamListToFilter, prevSelectedTeamIds, recipients) => {
     return teamListToFilter
          .filter(
            (team) =>
              prevSelectedTeamIds.includes((id) => id === team.teamId) ||
              personsByTeam[team.teamId].some((person) =>
                recipients.includes(person.personId),
              ),
          )
          .map((team) => team.teamId)
          .filter((teamId) => isTeamComplete(teamId, recipients));
  }, [personsByTeam, isTeamComplete]);

  const filterLocationIdsByPerson = useCallback((locationListToFilter, prevSelectedLocationIds, recipients) => {
            return locationListToFilter
          .filter(
            (location) =>
              prevSelectedLocationIds.includes((id) => id === location.locationId) ||
              personsByLocation[location.locationId].some((person) =>
                recipients.includes(person.personId),
              ),
          )
          .map((location) => location.locationId)
          .filter((locationId) => isLocationComplete(locationId, recipients));
  }, [personsByLocation, isLocationComplete]);

  const setRecipients = useCallback(
    (recipients) => {
      setAlertData((prev) => {
        return { ...prev, recipients };
      });

      //this logic handles the cases where a team selection causes a place to need to be selected and vice versa.
      setSelectedTeamIds((prev) => {
        return filterTeamIdsByPerson(teamList, prev, recipients);
      });

      setSelectedLocationIds((prev) => {
        return filterLocationIdsByPerson(locationList, prev, recipients);
      });
    },
    [
      setAlertData,
      teamList,
      locationList,
      filterLocationIdsByPerson,
      filterTeamIdsByPerson
    ],
  );

  useEffect(() => {
    // NEED TO FIX: currently setting state in Effect, linter complains, needed for using this component as an upsert
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasUnsavedChangesInfo(false);
    setHasOpenedSelectionModal(false);
    setNewDescription("");

    const alertExists = !!existingAlert;
    const hasHistory =
      (existingAlert?._associations?.descriptionHistory?.length || 0) > 0;
    setIsDetailsEditorShown(!hasHistory);

    const preSelectedTeamIds = alertExists ? extractTeamIds(existingAlert) : [];
    const preSelectedLocationIds = alertExists
      ? extractLocationIds(existingAlert)
      : [];

    setSelectedTeamIds(preSelectedTeamIds);
    setSelectedLocationIds(preSelectedLocationIds);

    const existingAffectedSystems = alertExists
      ? extractAffectedSystems(existingAlert)
      : [];

    const preSelectedSystemIds = existingAffectedSystems.map(
      (system) => system.organizationSystemId,
    );

    setSelectedSystemIds(alertExists ? preSelectedSystemIds : []);

    const systemStatusMap = new Map();

    organizationSystemMap.forEach((system, systemId) => {
      const isAffected = existingAffectedSystems.some(
        (affected) => affected.organizationSystemId === systemId,
      );

      systemStatusMap.set(systemId, {
        previous: !!system.isOnline,
        current: !!system.isOnline,
        isAffected,
      });
    });

    setSystemIsOnlineMap(Object.fromEntries(systemStatusMap));

    setLinks(alertExists ? existingAlert._associations?.links || [] : []);

    const defaultAlertData = {
      ...(alertExists ? { alertId: existingAlert.alertId } : {}),
      title: alertExists ? existingAlert.title : null,
      description: alertExists ? existingAlert.description : null,
      startDatetime: alertExists ? existingAlert.startDatetime : new Date(),
      expirationDatetime: alertExists
        ? existingAlert.expirationDatetime || null
        : null,
      infoType: alertExists
        ? existingAlert.infoType || INFO_TYPE_REMINDER
        : INFO_TYPE_REMINDER,
      recipients:
        Object.values(existingAlert?._associations?.recipientObjects || {}).map(
          (person) => person.personId,
        ) || [],
    };

    setAlertData(defaultAlertData);
  }, [visible, organizationSystemMap, personList, teamList, existingAlert]);

  const affectedSystems = useMemo(() => {
    const result = [];

    for (const [systemId, status] of Object.entries(systemIsOnlineMap)) {
      if (status.isAffected) {
        result.push([systemId, status]);
      }
    }

    return result;
  }, [systemIsOnlineMap]);

  const updateAlertData = useCallback((newData) => {
    setAlertData((previousAlertData) => ({
      ...previousAlertData,
      ...newData,
    }));

    setHasUnsavedChangesInfo(true);
  }, []);

  const isValid = useMemo(
    () => alertData.title?.trim().length > 0 && alertData.recipients.length > 0,
    [alertData],
  );

  const onClose = useCallback(() => {
    getAlerts(showDeleted);
    parentOnClose();
  }, [getAlerts, parentOnClose, showDeleted]);

  const onSend = useCallback(async () => {
    const payload = {
      alert: {
        alertId: alertData.alertId,
        isActive: true,
        title: alertData.title,
        description: newDescription?.trim() ? newDescription : null,
        startDateTime: formatDatetime(alertData.startDatetime),
        type: alertType,
        ...(isInfoMessage && {
          infoType: alertData.infoType || INFO_TYPE_REMINDER,
          expirationDatetime: formatDatetime(alertData.expirationDatetime),
        }),
      },
      links: isInfoMessage ? links.filter((link) => link.url?.trim()) : [],
      recipientPersonIds: alertData.recipients,
      affectedSystems: isInfoMessage
        ? []
        : affectedSystems.map(([organizationSystemId, status]) => ({
            organizationSystemId,
            isOnline: status.current,
          })),
      teamIds: selectedTeamIds,
      locationIds: selectedLocationIds,
    };

    setIsSending(true);
    try {
      await AlertService.upsertAlert(payload);
      await getOrganizationSystems();
      onClose();
    } catch (error) {
      if (__DEV__) {
        console.error("Failed to save alert:", error);
      }
      showToast("Something went wrong while saving the alert.", "error");
    } finally {
      setIsSending(false);
    }
  }, [
    alertData,
    alertType,
    affectedSystems,
    links,
    onClose,
    showToast,
    selectedTeamIds,
    selectedLocationIds,
    getOrganizationSystems,
    isInfoMessage,
    newDescription,
  ]);

  const handleCancelRequest = useCallback(() => {
    showConfirmation({
      prompt: existingAlert
        ? "Are you sure you want to cancel editing your alert?"
        : "Are you sure you want to cancel creating your alert?",
      onConfirm: () => onClose(),
    });
  }, [showConfirmation, onClose, existingAlert]);

  const handleLinkLabelChange = useCallback(
    (index, text) => {
      setLinks((prev) =>
        prev.map((linkItem, idx) =>
          idx === index ? { ...linkItem, label: text } : linkItem,
        ),
      );
      setHasUnsavedChangesInfo(true);
    },
    [setLinks, setHasUnsavedChangesInfo],
  );

  const handleLinkUrlChange = useCallback(
    (index, text) => {
      setLinks((prev) =>
        prev.map((linkItem, idx) =>
          idx === index ? { ...linkItem, url: text } : linkItem,
        ),
      );
      setHasUnsavedChangesInfo(true);
    },
    [setLinks, setHasUnsavedChangesInfo],
  );

  const handleRemoveLink = useCallback(
    (index) => {
      setLinks((prev) => prev.filter((__, idx) => idx !== index));
      setHasUnsavedChangesInfo(true);
    },
    [setLinks, setHasUnsavedChangesInfo],
  );

  return (
    <CustomModal
      open={visible}
      canSave={isValid && !isSending}
      isSaving={isSending}
      saveTitle={conditionalButtonTitle}
      onClose={hasUnsavedChangesInfo ? handleCancelRequest : onClose}
      onSave={onSend}
      title={conditionalTitle}
      scroll
    >
      <View style={classes.root}>
        <View>
          {!isInfoMessage && (
            <CustomDateTimePicker
              title="Start Time"
              onDateChange={(startDatetime) =>
                updateAlertData({ startDatetime })
              }
              value={alertData.startDatetime}
            />
          )}
          <LabelInput
            label={isInfoMessage ? "Info Message Title" : "Alert"}
            value={alertData.title}
            onChangeText={(title) =>
              updateAlertData({ title: title.slice(0, 255) })
            }
            required
            maxLength={255}
          />
          {descriptionHistory.length > 0 && (
            <View style={classes.historySection}>
              <Text style={classes.title}>Details</Text>
              {descriptionHistory.map((version) => (
                <View
                  key={version.alertDescriptionId}
                  style={classes.historyItem}
                >
                  <CustomTextEditor
                    readOnly
                    initialText={version.description}
                  />
                  <Text style={classes.historyMeta}>
                    {formatEditMeta(version)}
                  </Text>
                </View>
              ))}
            </View>
          )}
          {isDetailsEditorShown ? (
            <CustomTextEditor
              title="New Details"
              border
              initialText=""
              onChangeText={(description) => {
                setNewDescription(description);
                setHasUnsavedChangesInfo(true);
              }}
            />
          ) : (
            <Pressable onPress={() => setIsDetailsEditorShown(true)}>
              <Text style={classes.addDetailsText}>+ Add New Details</Text>
            </Pressable>
          )}
          {isInfoMessage && (
            <View style={classes.linksSection}>
              <Text style={classes.linksSectionTitle}>Links</Text>
              {links.map((link, index) => (
                <View key={link.id || index} style={classes.linkRow}>
                  <TextInput
                    style={classes.linkInput}
                    placeholder="Label"
                    value={link.label}
                    onChangeText={(text) => handleLinkLabelChange(index, text)}
                  />
                  <TextInput
                    style={classes.linkInput}
                    placeholder="URL or phone number"
                    value={link.url}
                    onChangeText={(text) => handleLinkUrlChange(index, text)}
                  />
                  <Pressable
                    onPress={() => handleRemoveLink(index)}
                  >
                    <Text style={classes.removeLinkText}>✕</Text>
                  </Pressable>
                </View>
              ))}
              <Pressable
                onPress={() =>
                  setLinks((prev) => [...prev, { id: Date.now(), label: "", url: "" }])
                }
              >
                <Text style={classes.addLinkText}>+ Add Link</Text>
              </Pressable>
            </View>
          )}
        </View>

        <View>
          <Text style={classes.title}>Select Recipients by</Text>
          <View style={classes.userSelectionContainer}>
            <CustomButton
              title="Places"
              variant="dark"
              onPress={() => setIsLocationSelectionOpen(true)}
              icon={<LocationIcon style={classes.icon} />}
              isIconSvg
            />
            <CustomButton
              title="Teams"
              variant="dark"
              onPress={() => setIsTeamSelectionOpen(true)}
              icon={<TeamIcon style={classes.icon} />}
              isIconSvg
            />
            {!isInfoMessage && (
              <CustomButton
                title="Platforms"
                variant="dark"
                onPress={() => setIsSystemSelectionOpen(true)}
                icon={<DataIcon style={classes.icon} />}
                isIconSvg
              />
            )}
          </View>
          {!isInfoMessage && (
            <View>
              <Text style={classes.title}>Affected Platforms</Text>
              <ScrollView style={classes.affectedPlatformsContainer}>
                {affectedSystems.length > 0 ? (
                  affectedSystems.map(([systemId, status]) => (
                    <Text key={systemId} style={classes.content}>
                      {organizationSystemMap.get(systemId).title}
                      {status.previous !== status.current && (
                        <>
                          {" "}
                          (status will be changed to{" "}
                          <Text
                            style={{
                              color: status.current
                                ? theme.color.green
                                : theme.color.red,
                            }}
                          >
                            {status.current ? "online" : "offline"}
                          </Text>
                          )
                        </>
                      )}
                    </Text>
                  ))
                ) : (
                  <Text style={[classes.content, classes.noneText]}>
                    No platforms affected
                  </Text>
                )}
              </ScrollView>
            </View>
          )}
          <AlertRecipientSummary
            onPress={() => setIsUserSelectionOpen(true)}
            teamIds={selectedTeamIds}
            locationIds={selectedLocationIds}
            userIds={alertData.recipients}
          />
        </View>
      </View>

      <SystemSelectionModal
        open={isSystemSelectionOpen}
        onClose={(newSelectedSystemIds) => {
          setIsSystemSelectionOpen(false);

          if (hasOpenedSelectionModal || newSelectedSystemIds.length > 0) {
            setHasUnsavedChangesInfo(true);
            setHasOpenedSelectionModal(true);
          }
        }}
        selectedSystemIds={selectedSystemIds}
        setSelectedSystemIds={setSelectedSystemIds}
        systemIsOnlineMap={systemIsOnlineMap}
        onChangeSystemStatus={(systemId, isOnline, isAffected) => {
          setSystemIsOnlineMap((previousMap) => ({
            ...previousMap,
            [systemId]: {
              previous: previousMap[systemId].previous,
              current: isOnline,
              isAffected:
                isAffected || previousMap[systemId].previous !== isOnline,
            },
          }));
          setSelectedSystemIds((previousSelected) => {
            const currentSet = new Set(previousSelected);
            currentSet.add(systemId);
            return [...currentSet];
          });
        }}
        selectedTeamIds={selectedTeamIds}
        setSelectedTeamIds={setSelectedTeamIds}
        selectedLocationIds={selectedLocationIds}
        setSelectedLocationIds={setSelectedLocationIds}
        recipients={alertData.recipients || []}
        setRecipients={setRecipients}
      />
      <TeamSelectionModal
        open={isTeamSelectionOpen}
        onClose={(newSelectedTeamIds) => {
          setIsTeamSelectionOpen(false);

          if (hasOpenedSelectionModal || newSelectedTeamIds.length > 0) {
            setHasOpenedSelectionModal(true);
            setHasUnsavedChangesInfo(true);
            /*
             * Note: recipients are already managed by the modal via setRecipients
             * Don't call handleSelectionFromTeams here as it would override individual user selections
             */
          }
        }}
        selectedTeamIds={selectedTeamIds}
        setSelectedTeamIds={setSelectedTeamIds}
        recipients={alertData.recipients}
        setRecipients={setRecipients}
      />
      <LocationSelectionModal
        open={isLocationSelectionOpen}
        onClose={(newSelectedLocationIds) => {
          setIsLocationSelectionOpen(false);

          if (hasOpenedSelectionModal || newSelectedLocationIds.length > 0) {
            setHasOpenedSelectionModal(true);
            setHasUnsavedChangesInfo(true);
          }
        }}
        selectedLocationIds={selectedLocationIds}
        setSelectedLocationIds={setSelectedLocationIds}
        recipients={alertData.recipients}
        setRecipients={setRecipients}
      />
      <UserSelectionModal
        open={isUserSelectionOpen}
        onClose={() => {
          setIsUserSelectionOpen(false);
        }}
        selectedUserIds={alertData.recipients}
        setSelectedUserIds={(newSelectedUserIds) => {
          setHasUnsavedChangesInfo(true);
          setHasOpenedSelectionModal(true);
          setRecipients(newSelectedUserIds);
        }}
      />
    </CustomModal>
  );
}

CreateAlertModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  existingAlert: PropTypes.object,
  alertType: PropTypes.string,
  showDeleted: PropTypes.bool,
};
